import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useAppSelector } from "../../../../app/hooks";
import { UserRole } from "../../../../app/userSlice";
import { useScrollToSection } from "../../../../app/useScrollToSection";
import { getParamAsInt } from "../../../../app/utils";
import { AvatarWithName } from "../../../../components/Avatar";
import RtkqResultViewer from "../../../../components/RtkqResultViewer";
import { SelectDevelopmentView } from "../../../../components/input/SelectDevelopmentView";
import { Loading } from "../../../../components/Loading";
import { DownloadButton } from "../../shared/DownloadButton";
import { LessonTable } from "./LessonTable";
import { SummaryTable } from "./SummaryTable";
import { WritingTable } from "./WritingTable";
import { ReviewsGivenTable } from "./ReviewsGivenTable";
import { ReviewsReceivedTable } from "./ReviewsReceivedTable";
import {
	useWriterUsageReportQuery,
	useGetTutorialHistoryQuery,
} from "./studentUsageReportApiSlice";
import { LessonChart } from "./LessonChart";

export const USAGE_SUMMARY = "usage-summary";
export const USAGE_WRITING = "usage-writing";
export const USAGE_WRITING_ESSAY = "usage-writing-essay";
export const USAGE_WRITING_CR = "usage-writing-cr";
export const USAGE_PEER_REVIEWS = "usage-peer-reviews";
export const USAGE_REVIEWS_GIVEN = "usage-peer-reviews-given";
export const USAGE_REVIEWS_RECEIVED = "usage-peer-reviews-received";
export const USAGE_LESSONS = "usage-lessons";

export default function StudentUsageReport() {
	const [queryParams] = useSearchParams();
	const writerIdQueryParam = getParamAsInt(queryParams, "writerId");
	const courseIdQueryParam = getParamAsInt(queryParams, "courseId");

	const { aspUrl } = useAppSelector((state) => state.site);
	const { userId, role, course } = useAppSelector((state) => state.user);

	const writerId = writerIdQueryParam ?? userId!;
	const courseId = courseIdQueryParam ?? course!.id;

	const [view, setView] = useState<string>("legacy");
	const [categoryId, setCategoryId] = useState<number>();

	const result = useWriterUsageReportQuery({ writerId, courseId });
	const data = result.data;

	const tutorialUsageResult = useGetTutorialHistoryQuery({ writerId, courseId });
	const tutorialUsage = tutorialUsageResult.data;

	useScrollToSection();

	function handleLessonChartClick(categoryId?: number) {
		setCategoryId(categoryId);
	}

	function handleLessonTableClick(categoryId?: number) {
		setCategoryId(categoryId);
	}

	const Heading = () => <h1>{role !== UserRole.Writer ? "Student " : ""}Usage Report</h1>;
	const Subheading = () =>
		role === UserRole.Writer || !data ? (
			""
		) : (
			<h2 className="mb-4 font-medium">
				<AvatarWithName
					userId={data.writerId}
					name={data.writerName}
					initials={data.writerInitials ?? "?"}
					hasImage={data.writerHasImage}
				/>
			</h2>
		);

	return (
		<section>
			<div className="flex justify-between">
				<div className="w-3/4">
					<Subheading />
					<Heading />
				</div>
				<SelectDevelopmentView
					value={view}
					setValue={(v) => setView(v as string)}
					simpleListBoxId="viewFilter"
					options={[
						{ value: "legacy", label: "Legacy" },
						{ value: "raw", label: "Raw Data" },
					]}
					fieldClasses="w-64"
				/>
			</div>
			{view == "legacy" && data && (
				<div className="mt-4">
					<p>
						The report below shows the total number of essays and draft submissions, the
						average number of drafts submitted per essay, the total number of peer
						reviews given and received, and the total number of minutes spent on
						lessons. To see a list of essays completed, click on the Essays link. To see
						the Peer Review Reports, click on the Peer Reviews link. To see Lesson
						Usage, click on the Lesson Usage link.
					</p>
					<section className="pt-4">
						<h2>Summary</h2>
						<SummaryTable
							className="mt-4 mx-auto"
							summary={data.summary}
							constructedResponseSummary={data.constructedResponseSummary}
							peerReviewsGiven={data.peerReviewsGiven}
							peerReviewsReceived={data.peerReviewsReceived}
							tutorialUsageForCourse={data.tutorialUsageForCourse}
						/>
					</section>
					<section id={USAGE_WRITING} className="pt-8">
						<h2>Writing</h2>
						<p className="mt-4">
							The reports below shows the date of the most recent draft submission,
							the prompt name, and the number of drafts submitted for the essay or
							response. To view the most recent Score Report for an essay or response,
							click on the date.
						</p>

						<h3 id={USAGE_WRITING_ESSAY} className="mt-4">
							Essays
						</h3>
						<Loading loading={result.isLoading} />
						{data && (
							<>
								<div className="flex place-content-end w-full">
									<DownloadButton
										url={`${aspUrl}/Report/DownloadEssayUsageFilteredByWriter?courseId=${courseId}&writerId=${writerId}`}
										disabled={data.essays.length == 0}
									/>
								</div>
								<WritingTable className="mt-4" essays={data.essays} />
							</>
						)}

						<h3 id={USAGE_WRITING_CR} className="mt-8">
							Constructed Response
						</h3>
						<Loading loading={result.isLoading} />
						{data && (
							<>
								<div className="flex place-content-end w-full">
									<DownloadButton
										url={`${aspUrl}/Report/DownloadConstructedResponseEssayUsageFilteredByWriter?courseId=${courseId}&writerId=${writerId}`}
										disabled={data.constructedResponseEssays.length == 0}
									/>
								</div>
								<WritingTable
									className="mt-4"
									essays={data.constructedResponseEssays}
								/>
							</>
						)}
					</section>
					<section id={USAGE_PEER_REVIEWS} className="pt-8">
						<h2>Peer Reviews</h2>
						<p className="mt-4">
							The “Reviews Given” report below shows the peer reviews that you have
							completed for members of your group. The report includes the date of the
							review, the name of the prompt, and the author of the essay that you
							reviewed. To view the review, click on the date. The “Reviews Received”
							report below shows the peer reviews that you have received from members
							of your group. The report includes the date of the review, the name of
							the prompt, and the name of the reviewer. To view the review, click on
							the date.
						</p>

						<h3 id={USAGE_REVIEWS_GIVEN} className="mt-4">
							Reviews Given
						</h3>
						<Loading loading={result.isLoading} />
						{data && (
							<>
								<div className="flex place-content-end w-full">
									<DownloadButton
										url={`${aspUrl}/Report/DownloadGivenPeerReviewUsageFilteredByWriter?courseId=${courseId}&writerId=${writerId}`}
										disabled={data.peerReviewsGiven.length == 0}
									/>
								</div>
								<ReviewsGivenTable
									className="mt-4"
									peerReviewsGiven={data.peerReviewsGiven}
								/>
							</>
						)}

						<h3 id={USAGE_REVIEWS_RECEIVED} className="mt-8">
							Reviews Received
						</h3>
						<Loading loading={result.isLoading} />
						{data && (
							<>
								<div className="flex place-content-end w-full">
									<DownloadButton
										url={`${aspUrl}/Report/DownloadReceivedPeerReviewUsageFilteredByWriter?courseId=${courseId}&writerId=${writerId}`}
										disabled={data.peerReviewsReceived.length == 0}
									/>
								</div>
								<ReviewsReceivedTable
									className="mt-4"
									peerReviewsReceived={data.peerReviewsReceived}
								/>
							</>
						)}
					</section>
					<section id={USAGE_LESSONS} className="pt-8">
						<h2>Lessons</h2>
						<p className="mt-4">
							The report below shows the most recent date of use and the total time
							spent using the lessons within each category. Click on the category
							title to view the dates of use and time spent on specific lessons within
							each area.
						</p>
						<Loading loading={result.isLoading || tutorialUsageResult.isLoading} />
						{data && tutorialUsage && (
							<>
								<div className="flex place-content-end w-full">
									{categoryId ? (
										<DownloadButton
											url={`${aspUrl}/Report/DownloadTutorialUsageFilteredByWriterAndFocus?categoryId=${categoryId}&writerId=${writerId}&courseId=${courseId}`}
											disabled={data.tutorials.length == 0}
										/>
									) : (
										<DownloadButton
											url={`${aspUrl}/Report/DownloadLessonUsageFilteredByWriter?courseId=${courseId}&writerId=${writerId}`}
											disabled={data.tutorials.length == 0}
										/>
									)}
								</div>
								<LessonTable
									className="mt-4"
									categoryUsage={data}
									tutorialUsage={tutorialUsage.tutorialsByCategory}
									categoryId={categoryId}
									onClick={handleLessonTableClick}
								/>

								{data.tutorials.length > 0 && (
									<LessonChart
										className="mt-8 pb-4 border-2 border-gray-200"
										categoryUsage={data}
										tutorialUsage={tutorialUsage.tutorialsByCategory}
										categoryId={categoryId}
										onClick={handleLessonChartClick}
									/>
								)}
							</>
						)}
					</section>
				</div>
			)}
			{view == "raw" && (
				<>
					<RtkqResultViewer heading="Usage Report Data" result={result} />
					<RtkqResultViewer heading="Tutorials Usage Data" result={tutorialUsageResult} />
				</>
			)}
		</section>
	);
}
